import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  UserPlus,
  MessageCircle,
  Headphones,
  ClipboardList,
  Video,
  Newspaper,
  Languages,
  FileCheck2,
  LifeBuoy,
  RefreshCcw,
  BellRing,
  HeartPulse,
  CalendarClock,
  Receipt,
  Scale,
  BarChart3,
  Users,
  Clapperboard,
  Wallet,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatedSection, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO, { SITE_URL, breadcrumbs } from "@/components/SEO";

const PATH = "/industries/edtech";
const IMG = "/industries-images/edtech";

const heroStats = [
  { val: "16", label: "Edtech workflows we automate end to end" },
  { val: "70%", label: "Average efficiency gain on automated workflows" },
  { val: "60%", label: "Faster query resolution in Hindi and English" },
  { val: "85%", label: "Less manual data entry across forms and invoices" },
];

const levers = [
  {
    icon: Users,
    title: "Admissions and sales",
    body: "Leads from YouTube, the app and ads wait hours for a counsellor. Follow-ups, payment links and batch allocation are done by hand, so headcount grows with every campaign.",
  },
  {
    icon: Clapperboard,
    title: "Content operations",
    body: "Every live class is re-cut, re-typed into notes, turned into MCQs and translated by a team of editors. Output is capped by how many people you can hire.",
  },
  {
    icon: Wallet,
    title: "Support and back office",
    body: "Access issues, refunds, faculty payouts, invoices and GST reconciliation run on spreadsheets and inboxes, and the cost to serve each student rises with scale.",
  },
];

const stages = [
  {
    id: "admissions",
    label: "Admissions and sales",
    kicker: "Workflows 01 to 04",
    title: "Respond to every lead in minutes, not hours",
    intro:
      "The cheapest enrolment is the lead you already paid for. Four workflows that make sure no lead waits and every counsellor hour goes to a real conversation.",
    image: `${IMG}/admissions.jpg`,
    offerings: [
      {
        num: "01",
        icon: UserPlus,
        title: "Lead capture, scoring and routing",
        problem: "Leads arrive from YouTube, the app, ads and webinars into different sheets, and counsellors call them in the order they happen to see them.",
        build:
          "One pipeline that captures every lead the moment it arrives, de-duplicates it, enriches it with app, video and test behaviour, scores its likelihood to enrol and routes it to the right counsellor in your CRM.",
        outcomes: ["Every lead in the CRM instantly", "Counsellors call the hottest leads first", "No lead left untouched"],
      },
      {
        num: "02",
        icon: MessageCircle,
        title: "WhatsApp follow-up and payment links",
        problem: "Reminders, brochures, fee details and payment links are sent manually, and follow-up stops after the second attempt.",
        build:
          "An AI assistant on WhatsApp that answers course and fee questions in Hindi and English, sends brochures, demo-class links and payment links, and keeps following up on a schedule until the learner enrols or opts out. Hands over to a counsellor with the full chat attached.",
        outcomes: ["24x7 replies in Hindi and English", "Follow-ups that never stop early", "Payment links sent at the right moment"],
      },
      {
        num: "03",
        icon: Headphones,
        title: "Counsellor call summaries and QA",
        problem: "Managers listen to a handful of sales calls a week, and objections and mis-selling go unnoticed.",
        build:
          "Every counselling call transcribed in Hindi, English and Hinglish, summarised into the CRM, and scored against your script. Daily flags, objection trends and coaching clips for team leads.",
        outcomes: ["100% of calls summarised", "CRM notes filled automatically", "Coaching based on real calls"],
      },
      {
        num: "04",
        icon: ClipboardList,
        title: "Enrolment and batch allocation",
        problem: "After payment, an ops team creates the account, assigns the batch and sends the welcome kit by hand.",
        build:
          "The moment a payment clears, the student is created in the LMS, assigned to the right batch and language, added to groups and sent onboarding messages, with exceptions sent to a person for approval.",
        outcomes: ["Zero manual enrolment entry", "Batch allocated in seconds", "Clean data from day one"],
      },
    ],
  },
  {
    id: "content",
    label: "Content operations",
    kicker: "Workflows 05 to 08",
    title: "More content from the same faculty and editors",
    intro:
      "Content is the product, and most of the work around a class is repetitive. Four workflows that turn every hour of teaching into many hours of material.",
    image: `${IMG}/content.jpg`,
    offerings: [
      {
        num: "05",
        icon: Video,
        title: "Class to notes, MCQs and shorts",
        problem: "Editors re-watch every live class to write notes, frame questions and cut highlight clips.",
        build:
          "Each recorded class is transcribed and turned into structured notes, MCQs with answers and explanations, flashcards and short-video cut points, ready for faculty to review and publish.",
        outcomes: ["Notes and MCQs within hours of class", "Shorts cut points suggested", "Faculty review, not rewrite"],
      },
      {
        num: "06",
        icon: Newspaper,
        title: "Current affairs drafting",
        problem: "A team reads newspapers and government sources every morning to write the daily current affairs PDF and quiz.",
        build:
          "Daily drafts of current affairs summaries, exam-relevant notes and quizzes from trusted sources, tagged by subject and exam, with sources cited for faculty to verify and publish.",
        outcomes: ["Daily drafts ready by morning", "Tagged by exam and subject", "Sources cited for review"],
      },
      {
        num: "07",
        icon: Languages,
        title: "Translation and regional versions",
        problem: "Each course has to be re-created in Hindi and regional languages, so most content ships in only one or two.",
        build:
          "Translation of notes, tests and subtitles into Hindi and regional languages using exam-specific glossaries, plus voice dubbing for videos, with a reviewer approving each piece.",
        outcomes: ["New languages without new teams", "Exam terms translated consistently", "Reviewer in the loop"],
      },
      {
        num: "08",
        icon: FileCheck2,
        title: "Test paper checking",
        problem: "Wrong answer keys and duplicate questions reach students and trigger complaints and re-tests.",
        build:
          "Automated checks on every mock test before release: answer-key verification, duplicate and near-duplicate detection, difficulty balance and syllabus coverage, with issues flagged for the setter.",
        outcomes: ["Fewer answer-key errors", "Duplicates caught before release", "Balanced, on-syllabus papers"],
      },
    ],
  },
  {
    id: "support",
    label: "Student support",
    kicker: "Workflows 09 to 12",
    title: "Resolve most student requests without a ticket queue",
    intro:
      "Support volume grows with every batch. Four workflows that answer, fix and follow up automatically, and send only real exceptions to your team.",
    image: `${IMG}/support.jpg`,
    offerings: [
      {
        num: "09",
        icon: LifeBuoy,
        title: "Query and ticket resolution",
        problem: "Login, schedule, recording and certificate questions fill the helpdesk and WhatsApp in several languages.",
        build:
          "An AI agent on WhatsApp, in-app chat and email that answers from your FAQs and policies and checks the student's account in the LMS before replying. Anything it cannot resolve becomes a ticket with the full context.",
        outcomes: ["Most queries resolved instantly", "Hindi and English support", "Tickets arrive with full context"],
      },
      {
        num: "10",
        icon: RefreshCcw,
        title: "Access, refund and transfer requests",
        problem: "Course access fixes, batch transfers and refunds bounce between support, ops and finance for days.",
        build:
          "Requests checked against your policy and the student's payment and usage history, fixed automatically when they qualify and routed for approval when they do not, with the student updated at each step.",
        outcomes: ["Policy applied consistently", "Days cut to minutes", "Every decision logged"],
      },
      {
        num: "11",
        icon: BellRing,
        title: "Onboarding and class reminders",
        problem: "Students miss the first classes and tests because reminders go out late or not at all.",
        build:
          "Personalised onboarding journeys and reminders for classes, tests and deadlines on WhatsApp, SMS and email, triggered by the timetable and each student's activity.",
        outcomes: ["Higher first-week attendance", "Reminders that follow the timetable", "No manual broadcast lists"],
      },
      {
        num: "12",
        icon: HeartPulse,
        title: "Feedback and churn alerts",
        problem: "Drop-off is noticed at renewal time, when it is too late to win the student back.",
        build:
          "Feedback collected after classes and tests, plus a model that flags students whose attendance, scores or engagement are slipping, so counsellors and mentors can step in before renewal.",
        outcomes: ["At-risk students flagged early", "Feedback summarised for faculty", "Higher renewals"],
      },
    ],
  },
  {
    id: "finance",
    label: "Finance and HR ops",
    kicker: "Workflows 13 to 16",
    title: "Payouts, books and reports that run themselves",
    intro:
      "Faculty payouts, invoices, payment gateways and GST create a monthly scramble. Four workflows that close it with people approving only exceptions.",
    image: `${IMG}/finance.jpg`,
    offerings: [
      {
        num: "13",
        icon: CalendarClock,
        title: "Faculty scheduling and payouts",
        problem: "Class schedules, attendance and per-session rates are tracked in sheets, and payouts are calculated by hand every month.",
        build:
          "Faculty schedules generated from batch plans and availability, sessions logged from the LMS, and payouts calculated by your rate rules, with statements sent to each faculty member and posted to accounts.",
        outcomes: ["Payouts calculated automatically", "Fewer disputes with faculty", "Schedules without clashes"],
      },
      {
        num: "14",
        icon: Receipt,
        title: "Invoice processing",
        problem: "Vendor, marketing and studio invoices arrive as PDFs and emails and are keyed into accounts by hand.",
        build:
          "Document AI that reads invoices in any format, validates them against purchase orders and GST rules, and posts them to Tally, Zoho or your ERP for approval.",
        outcomes: ["85% less manual data entry", "Invoice backlog cleared", "GST checks built in"],
      },
      {
        num: "15",
        icon: Scale,
        title: "Payments and GST reconciliation",
        problem: "Gateway settlements, refunds, EMIs and GST filings are matched line by line in spreadsheets.",
        build:
          "Every gateway settlement, refund and EMI matched to the student and order, with mismatches flagged and GST data prepared for filing every month.",
        outcomes: ["Books reconciled daily", "Mismatches caught early", "Filing-ready GST data"],
      },
      {
        num: "16",
        icon: BarChart3,
        title: "Daily MIS and leadership reports",
        problem: "Analysts pull numbers from the CRM, LMS and payment systems to build the morning report by hand.",
        build:
          "Enrolments, revenue, collections, attendance and support metrics compiled every morning across systems, with a copilot that lets leaders ask questions of the data in plain language.",
        outcomes: ["Reports ready every morning", "One view across systems", "Ask your data anything"],
      },
    ],
  },
];

const roadmap = [
  { when: "Weeks 1 to 6", title: "Prove the number", body: "Lead capture and WhatsApp follow-up, or support ticket resolution, on one course, measured against the current process.", nums: ["01", "02", "09"] },
  { when: "Months 2 to 3", title: "Scale the front office", body: "Call summaries and QA, enrolment automation, refunds and transfers, reminders.", nums: ["03", "04", "10", "11"] },
  { when: "Months 3 to 5", title: "Multiply content", body: "Class to notes and MCQs, current affairs drafting, translation and test paper checking.", nums: ["05", "06", "07", "08"] },
  { when: "Month 5 onwards", title: "Close the books", body: "Faculty payouts, invoices, reconciliation, churn alerts and daily leadership reports.", nums: ["12", "13", "14", "15", "16"] },
];

const caseStudies = [
  {
    id: "secure-lms-drm",
    title: "Securing & Scaling Enterprise Learning",
    client: "Global Media & Education Conglomerate",
    description: "Enterprise LMS with secure DRM content protection, AI-driven assessment generation, marketing pipelines and learner dashboards.",
    metric: "80% faster setup",
  },
  {
    id: "ai-bilingual-chatbot",
    title: "AI Bilingual Chatbot",
    client: "Customer Service Platform",
    description: "AI support assistant answering customer queries in English and Hindi, with hand-off to human agents.",
    metric: "60% faster query resolution",
  },
  {
    id: "ai-invoicing-engine",
    title: "AI Invoicing Engine",
    client: "Global Enterprise",
    description: "Multi-format, multi-language invoice intake, extraction, validation and posting at enterprise volume.",
    metric: "Invoice backlog eliminated",
  },
];

const faqs = [
  {
    q: "Which edtech companies is this built for?",
    a: "Test-prep and exam-prep platforms, K-12 and higher-education companies, upskilling and certification providers, and coaching institutes running online and hybrid batches in India. We have already automated workflows for edtech companies, and every workflow can be started on its own.",
  },
  {
    q: "Do we need to change our CRM, LMS or helpdesk?",
    a: "No. The automations run on the tools you already use, such as LeadSquared, Salesforce, Zoho, Freshdesk, your LMS, WhatsApp Business, payment gateways and Tally, through APIs and secure integrations.",
  },
  {
    q: "Does student data leave our environment?",
    a: "No. Workflows can run in your cloud account, student data is never used to train anyone else's models, and you own the code, the models and the data.",
  },
  {
    q: "What does a first engagement look like?",
    a: "A short mapping session with your sales, content and support leads, then one workflow live on a pilot course in about six weeks, measured against the current process. Pricing is fixed and milestone-based.",
  },
  {
    q: "Will this replace our counsellors, faculty or support team?",
    a: "It takes over the repetitive work around them: data entry, follow-ups, first drafts and routine requests. Your people spend their time on conversations, teaching and the exceptions that need judgement, and the same team can serve far more students.",
  },
];

const description =
  "AI workflow automation for edtech: lead capture and routing, WhatsApp follow-ups, counsellor call QA, enrolment, class-to-notes and MCQs, current affairs, translation, test checking, student support, refunds, reminders, churn alerts, faculty payouts, invoices, GST reconciliation and daily MIS, built and deployed by VANCO.";

export default function EdtechPage() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "AI Workflow Automation for Edtech",
      serviceType: "AI workflow automation for edtech and test-prep companies",
      description,
      url: `${SITE_URL}${PATH}`,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: [{ "@type": "Country", name: "India" }, "Worldwide"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Edtech workflow automations",
        itemListElement: stages.flatMap((s) =>
          s.offerings.map((o) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: o.title } }))
        ),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    breadcrumbs([
      { name: "Home", path: "/" },
      { name: "Edtech AI Workflow Automation", path: PATH },
    ]),
  ];

  return (
    <div className="overflow-hidden bg-white text-slate-800" data-testid="industry-page" id="industry-page-edtech">
      <SEO title="AI Workflow Automation for Edtech | VANCO" description={description} path={PATH} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 bg-slate-950 text-white overflow-hidden" data-testid="industry-hero">
        <img
          src={`${IMG}/hero.jpg`}
          alt="Student studying in a library"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/60" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-white/10 border border-white/15 text-[#f7c550] rounded-full text-xs font-bold tracking-widest uppercase mb-6 font-['Inter']">
              Edtech and test prep
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope'] mb-6 leading-tight text-white">
              Automate the operations behind every learner, from first lead to final result
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-10 font-['Inter']">
              AI agents that take over the repetitive work in admissions, content, student support and finance, on the tools your teams already use. Already running at edtech companies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="rounded-full px-8 py-6 text-base font-bold bg-[#f7c550] hover:bg-[#f5b82e] text-slate-950 shadow-lg shadow-amber-500/20">
                  Talk to an expert
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#offerings">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-base font-semibold border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white">
                  See the 16 workflows
                  <ArrowDown className="ml-2 h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {heroStats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5">
                <div className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#f7c550]">{s.val}</div>
                <div className="text-sm text-slate-200 mt-1 font-['Inter']">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three levers */}
      <AnimatedSection className="py-24 bg-white" data-testid="edtech-levers">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Why now</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-5">
              At edtech scale, growth adds headcount. Automation breaks that link.
            </h2>
            <p className="text-lg text-slate-600 font-['Inter']">
              Counsellors chasing leads, editors re-typing classes and ops teams reconciling sheets. We automate that work inside the CRM, LMS, helpdesk and WhatsApp you already run.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-3 gap-6">
            {levers.map((l) => {
              const Icon = l.icon;
              return (
                <AnimatedItem key={l.title}>
                  <div className="h-full rounded-3xl border border-slate-200/70 bg-slate-50/60 p-8 hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0f172a] font-['Manrope'] mb-3">{l.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed font-['Inter']">{l.body}</p>
                  </div>
                </AnimatedItem>
              );
            })}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Stage navigation */}
      <section id="offerings" className="bg-[#243b7f] text-white py-16 scroll-mt-20" data-testid="edtech-offerings-nav">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">The workflows</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope']">
                Sixteen workflows, organised by the team they free up
              </h2>
            </div>
            <div className="lg:col-span-5">
              <ul className="flex flex-wrap gap-2">
                {stages.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="inline-block px-4 py-2 rounded-full border border-white/20 bg-white/5 hover:bg-[#f7c550] hover:text-slate-950 hover:border-[#f7c550] text-sm font-semibold transition-colors font-['Inter']"
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stages */}
      {stages.map((stage, si) => (
        <section
          key={stage.id}
          id={stage.id}
          className={`py-24 scroll-mt-20 ${si % 2 === 0 ? "bg-white" : "bg-slate-50/70"} border-t border-slate-100`}
          data-testid={`edtech-stage-${stage.id}`}
        >
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-4 lg:sticky lg:top-28">
                <div className="relative rounded-3xl overflow-hidden aspect-[3/2] mb-7 shadow-xl shadow-slate-200/70">
                  <img src={stage.image} alt={stage.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                  <span className="absolute left-5 bottom-5 inline-block px-3 py-1.5 bg-[#f7c550] text-slate-950 rounded-md text-xs font-bold uppercase tracking-wider font-['Inter']">
                    {stage.kicker}
                  </span>
                </div>
                <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-3 font-['Inter']">{stage.label}</p>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-4">{stage.title}</h2>
                <p className="text-slate-600 font-['Inter'] leading-relaxed">{stage.intro}</p>
              </div>

              <div className="lg:col-span-8 space-y-6">
                {stage.offerings.map((o) => {
                  const Icon = o.icon;
                  return (
                    <article
                      key={o.num}
                      className="group rounded-3xl bg-white border border-slate-200/70 p-7 md:p-9 shadow-sm hover:shadow-2xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-500"
                      data-testid={`edtech-offering-${o.num}`}
                    >
                      <div className="flex items-start gap-5">
                        <div className="shrink-0 w-14 h-14 rounded-2xl bg-[#243b7f] text-[#f7c550] flex items-center justify-center">
                          <Icon className="w-7 h-7" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-xs font-bold tracking-widest text-[#243b7f] font-['Inter']">{o.num}</span>
                            <span className="h-px flex-1 bg-slate-200" />
                          </div>
                          <h3 className="text-xl md:text-2xl font-bold text-[#0f172a] font-['Manrope'] mb-3 group-hover:text-[#243b7f] transition-colors">
                            {o.title}
                          </h3>
                          <p className="text-sm font-semibold text-red-600/90 mb-4 font-['Inter']">
                            <span className="uppercase tracking-wider text-[11px] mr-2">The problem</span>
                            <span className="text-slate-700 font-normal">{o.problem}</span>
                          </p>
                          <p className="text-slate-600 text-sm md:text-[15px] leading-relaxed font-['Inter'] mb-5">
                            <span className="uppercase tracking-wider text-[11px] font-bold text-[#243b7f] mr-2">What we automate</span>
                            {o.build}
                          </p>
                          <ul className="flex flex-wrap gap-2">
                            {o.outcomes.map((t) => (
                              <li key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold font-['Inter'] border border-emerald-100">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Roadmap */}
      <AnimatedSection className="py-24 bg-[#0f172a] text-white relative overflow-hidden" data-testid="edtech-roadmap">
        <img src={`${IMG}/students.jpg`} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/80 via-[#0f172a]/90 to-[#0f172a]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#f7c550] mb-4 font-['Inter']">Where to start</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-['Manrope'] mb-5">
              Start with one workflow, prove the number, then scale
            </h2>
            <p className="text-slate-300 text-lg font-['Inter']">
              Admissions follow-up or student support usually shows a measurable result within six weeks. Once the first workflow is live, the rest plug into the same integrations.
            </p>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((r, i) => (
              <AnimatedItem key={r.title}>
                <div className={`h-full rounded-3xl p-7 border ${i === 0 ? "bg-[#f7c550] text-slate-950 border-[#f7c550]" : "bg-white/5 border-white/10"}`}>
                  <div className={`text-xs font-bold tracking-widest uppercase mb-3 font-['Inter'] ${i === 0 ? "text-slate-800" : "text-[#f7c550]"}`}>{r.when}</div>
                  <h3 className="text-xl font-bold font-['Manrope'] mb-3">{r.title}</h3>
                  <p className={`text-sm leading-relaxed font-['Inter'] mb-5 ${i === 0 ? "text-slate-800" : "text-slate-300"}`}>{r.body}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {r.nums.map((n) => (
                      <span key={n} className={`px-2 py-1 rounded-md text-[11px] font-bold font-['Inter'] ${i === 0 ? "bg-slate-950/10 text-slate-900" : "bg-white/10 text-white"}`}>{n}</span>
                    ))}
                  </div>
                </div>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* Success stories */}
      <section className="py-24 bg-slate-50/50 border-t border-b border-slate-100" data-testid="industry-case-studies">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">Delivered work</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] font-['Manrope']">
              Proven in production, including at edtech companies
            </h2>
          </div>
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <AnimatedItem key={study.id}>
                <Link to={`/case-studies/${study.id}`} className="group block h-full">
                  <div className="h-full flex flex-col justify-between min-h-[320px] bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm hover:shadow-2xl hover:shadow-slate-200/50 hover:-translate-y-2 transition-all duration-500">
                    <div>
                      <div className="inline-block px-3 py-1.5 bg-[#f7c550] text-[#0f172a] font-bold text-xs uppercase tracking-wider rounded-md mb-6">Case Study</div>
                      <h3 className="text-[#0a122c] text-xl font-bold font-['Manrope'] mb-2 leading-snug group-hover:text-[#243b7f] transition-colors">{study.title}</h3>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 font-['Inter']">{study.client}</p>
                      <p className="text-slate-600 text-sm leading-relaxed font-['Inter'] mb-4">{study.description}</p>
                      <p className="text-[#243b7f] text-sm font-bold font-['Inter']">{study.metric}</p>
                    </div>
                    <span className="text-[#243b7f] font-semibold text-sm hover:underline inline-flex items-center gap-1 mt-6">
                      Read Now
                      <ArrowRight className="w-4 h-4 ml-0.5" />
                    </span>
                  </div>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-testid="edtech-faq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <p className="text-sm font-bold tracking-widest uppercase text-[#243b7f] mb-4 font-['Inter']">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-10">Questions edtech teams ask us</h2>
          <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
            {faqs.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none text-lg font-semibold text-[#0f172a] font-['Manrope']">
                  {f.q}
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed font-['Inter'] pr-12">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#243b7f]" data-testid="industry-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Manrope'] mb-6">
            Let's automate your first workflow
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto font-['Inter']">
            A 30-minute session to map where your teams spend their hours. Then we pick one workflow for a pilot and agree how the result gets measured.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105">
              Book the session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
